import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useSession } from "@/hooks/auth-hooks";
import { Logo } from "@/components/brand/logo";
import { BrandButton } from "@/components/brand/brand-button";

export const Route = createFileRoute("/invitations/$token")({
	component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
	const { token } = Route.useParams();
	const { session } = useSession();
	const navigate = useNavigate();

	const [status, setStatus] = useState<
		"loading" | "validating" | "accepting" | "error" | "accepted"
	>("loading");
	const [error, setError] = useState<string | null>(null);
	const [workspaceName, setWorkspaceName] = useState<string | null>(null);
	const [validated, setValidated] = useState(false);

	const validate = useCallback(async () => {
		if (validated) return;
		setValidated(true);
		setStatus("validating");

		try {
			const res = await fetch(
				`/api/workspaces/invitations/accept?token=${encodeURIComponent(token)}`,
			);
			const data = await res.json();

			if (!res.ok || !data.valid) {
				setError(
					data.expired
						? "This invitation has expired. Ask the workspace owner for a new one."
						: "This invitation is invalid or has already been used.",
				);
				setStatus("error");
				return;
			}

			setWorkspaceName(data.workspaceName);

			// If logged in, auto-accept
			if (session) {
				await acceptInvite();
			} else {
				setStatus("loading");
			}
		} catch {
			setError("Failed to validate invitation. Please try again.");
			setStatus("error");
		}
	}, [token, session, validated]);

	// Trigger validation on first render
	if (!validated) {
		validate();
	}

	async function acceptInvite() {
		setStatus("accepting");
		try {
			const res = await fetch("/api/workspaces/invitations/accept", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token }),
			});
			const data = await res.json();

			if (!res.ok) {
				setError(data.error || "Failed to accept invitation");
				setStatus("error");
				return;
			}

			setStatus("accepted");
			setTimeout(() => {
				navigate({ to: "/dashboard" });
			}, 1500);
		} catch {
			setError("Failed to accept invitation. Please try again.");
			setStatus("error");
		}
	}

	return (
		<main
			id="main-content"
			className="min-h-svh flex items-center justify-center px-4"
			style={{ backgroundColor: "#0a0a0a" }}
		>
			<div className="w-full max-w-md">
				<div className="text-center mb-8">
					<Logo size="sm" />
				</div>

				<div
					className="p-8"
					style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
				>
					<p className="text-[10px] font-bold uppercase tracking-[0.1em] mb-5" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>Team Invitation</p>
					{status === "validating" || status === "accepting" ? (
						<div className="text-center space-y-4">
							<div
								className="mx-auto h-8 w-8 animate-spin rounded-full border-2"
								style={{ borderColor: "rgba(234,40,4,0.2)", borderTopColor: "#6B3FF2" }}
							/>
							<p className="text-sm" style={{ color: "#888888" }}>
								{status === "validating"
									? "Validating invitation..."
									: "Accepting invitation..."}
							</p>
						</div>
					) : status === "error" ? (
						<div className="text-center space-y-4">
							<div
								className="w-16 h-16 flex items-center justify-center mx-auto"
								style={{ backgroundColor: "rgba(255,110,132,0.1)" }}
							>
								<span
									className="material-symbols-outlined"
									aria-hidden="true"
									style={{ fontSize: "32px", color: "#ff6e84", fontVariationSettings: "'FILL' 1" }}
								>
									cancel
								</span>
							</div>
							<h1
								className="text-2xl font-semibold tracking-tight"
								style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								Invalid invitation
							</h1>
							<p className="text-sm" style={{ color: "#888888" }}>{error}</p>
							<div className="pt-2">
								<BrandButton asChild className="w-full">
									<Link to="/">Go to homepage</Link>
								</BrandButton>
							</div>
						</div>
					) : status === "accepted" ? (
						<div className="text-center space-y-4">
							<div
								className="w-16 h-16 flex items-center justify-center mx-auto"
								style={{ backgroundColor: "rgba(43,154,102,0.1)" }}
							>
								<span
									className="material-symbols-outlined"
									aria-hidden="true"
									style={{ fontSize: "32px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}
								>
									check_circle
								</span>
							</div>
							<h1
								className="text-2xl font-semibold tracking-tight"
								style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								Welcome to the team!
							</h1>
							<p className="text-sm" style={{ color: "#888888" }}>
								Redirecting to your dashboard...
							</p>
						</div>
					) : !session ? (
						<div className="space-y-6">
							<div className="text-center space-y-2">
								<h1
									className="text-2xl font-semibold tracking-tight"
									style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									You&apos;ve been invited
									{workspaceName ? (
										<> to <span style={{ color: "#6B3FF2" }}>{workspaceName}</span></>
									) : null}
								</h1>
								<p className="text-sm" style={{ color: "#888888" }}>
									Sign in or create an account to accept this invitation.
								</p>
							</div>
							<div className="flex flex-col gap-3">
								<BrandButton asChild size="lg" className="w-full">
									<Link to="/login" search={{ redirect: `/invitations/${token}` }}>
										Sign in
									</Link>
								</BrandButton>
								<BrandButton asChild variant="outline" size="lg" className="w-full">
									<Link to="/signup" search={{ redirect: `/invitations/${token}` }}>
										Create account
									</Link>
								</BrandButton>
							</div>
						</div>
					) : (
						<div className="space-y-6 text-center">
							<div>
								<h1
									className="text-2xl font-semibold tracking-tight"
									style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									Join{" "}
									{workspaceName ? (
										<span style={{ color: "#6B3FF2" }}>{workspaceName}</span>
									) : (
										"workspace"
									)}
								</h1>
								<p className="mt-2 text-sm" style={{ color: "#888888" }}>
									Accept the invitation to start collaborating.
								</p>
							</div>
							<BrandButton
								type="button"
								onClick={acceptInvite}
								size="lg"
								className="w-full"
							>
								Accept invitation
							</BrandButton>
						</div>
					)}
				</div>
			</div>
		</main>
	);
}
